import { motion } from "framer-motion";
import { Layers, HelpCircle } from "lucide-react";

export default function DLC({ data }) {
  return (
    <section
      id="dlc"
      data-testid="dlc-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute -top-40 left-1/4 w-[500px] h-[500px] rounded-full bg-accent/[0.04] blur-3xl pointer-events-none" />

      <div className="max-w-[1600px] mx-auto relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14 md:mb-20"
        >
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-px bg-accent" />
              <span className="font-body text-[11px] uppercase tracking-[0.35em] text-accent">
                {data.eyebrow}
              </span>
            </div>
            <h2
              data-testid="dlc-title"
              className="font-heading uppercase text-white text-6xl md:text-7xl lg:text-8xl leading-[0.9]"
            >
              {data.title}
            </h2>
          </div>
          <p className="lg:col-span-4 font-body text-base md:text-lg text-white/60 leading-relaxed max-w-md">
            {data.description}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {data.items.map((item, i) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`dlc-item-${item.id}`}
              className="group relative border border-white/10 bg-[#09090B] aspect-[3/4] flex flex-col justify-between p-6 md:p-8 hover:border-accent/40 transition-colors duration-500 overflow-hidden"
            >
              <div
                className="absolute inset-0 opacity-[0.04] pointer-events-none group-hover:opacity-[0.08] transition-opacity duration-700"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(45deg, #ffffff 0, #ffffff 1px, transparent 1px, transparent 16px)",
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <HelpCircle size={140} className="text-white/[0.03]" strokeWidth={1} />
              </div>

              <div className="relative flex items-start justify-between">
                <span className="font-body text-[9px] uppercase tracking-[0.3em] text-accent">
                  {item.codename}
                </span>
                <Layers size={14} className="text-accent/70 group-hover:text-accent transition-colors" />
              </div>

              <div className="relative">
                <div className="font-heading uppercase text-white text-2xl md:text-3xl leading-tight mb-4 group-hover:text-accent transition-colors duration-500">
                  {item.title}
                </div>
                <div className="flex items-center gap-2 border-t border-white/10 pt-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                  <span className="font-body text-[9px] uppercase tracking-[0.3em] text-white/60">
                    {item.status}
                  </span>
                </div>
              </div>

              <span className="absolute top-0 left-0 w-8 h-8 border-t border-l border-accent scale-0 group-hover:scale-100 transition-transform duration-500 origin-top-left" />
              <span className="absolute bottom-0 right-0 w-8 h-8 border-b border-r border-accent scale-0 group-hover:scale-100 transition-transform duration-500 origin-bottom-right" />
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="mt-8 font-body text-[11px] uppercase tracking-[0.25em] text-white/40 leading-relaxed max-w-2xl"
        >
          {data.footnote}
        </motion.p>
      </div>
    </section>
  );
}
